import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
  useMediaQuery,
  DialogContentText,
} from '@mui/material';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DynamicTable from 'src/components/shared/DynamicTable';
import Services from 'src/services/Services';
import { tokenService } from 'src/services';

export default function FaqManagementPage() {
  const token = tokenService?.getUser()?.token;
  const isMobile = useMediaQuery('(max-width:768px)');

  // Seçilen tab: porter veya store (mağaza)
  const [selectedTab, setSelectedTab] = useState('porter');
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Ekle/Düzenle Dialog state'leri
  const [dialogOpen, setDialogOpen] = useState(false);
  const [modalType, setModalType] = useState('new'); // 'new' veya 'edit'
  const [selectedFaq, setSelectedFaq] = useState({
    title: '',
    description: '',
    type: selectedTab,
  });

  // Silme işlemi için dialog state'leri
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState(null);

  // Servisten FAQ verilerini çekme fonksiyonu
  const fetchFaqData = async () => {
    setLoading(true);
    try {
      const res = await Services.GetFaq(token, selectedTab);
      if (res.data.status) {
        setFaqData(res.data.result);
      } else {
        setFaqData([]);
      }
    } catch (error) {
      console.error(error);
      setFaqData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqData();
  }, [selectedTab, token]);

  // Tab geçişi
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  // Yeni kayıt için dialogu açma
  const handleOpenDialogForNew = () => {
    setModalType('new');
    setSelectedFaq({ title: '', description: '', type: selectedTab });
    setDialogOpen(true);
  };

  // Düzenleme için dialogu açma
  const handleOpenDialogForEdit = (faq) => {
    setModalType('edit');
    setSelectedFaq(faq);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Ekle/Düzenle işleminin servis çağrısı
  const handleDialogSubmit = async () => {
    if (modalType === 'new') {
      try {
        const res = await Services.CreateFaq(token, selectedFaq);
        if (res.data.status) {
          toast.success('İşlem başarıyla gerçekleşmiştir.');
          fetchFaqData();
          setDialogOpen(false);
        } else {
          toast.error('İşlem gerçekleştirilirken bir hata oluştu.');
        }
      } catch (error) {
        toast.error('İşlem gerçekleştirilirken bir hata oluştu.');
        console.error(error);
      }
    } else if (modalType === 'edit') {
      try {
        const res = await Services.UpdateFaq(
          token,
          selectedFaq.id,
          selectedFaq.title,
          selectedFaq.description,
          selectedFaq.type
        );
        if (res.data.status) {
          toast.success('İşlem başarıyla gerçekleşmiştir.');
          fetchFaqData();
          setDialogOpen(false);
        } else {
          toast.error('İşlem gerçekleştirilirken bir hata oluştu.');
        }
      } catch (error) {
        toast.error('İşlem gerçekleştirilirken bir hata oluştu.');
        console.error(error);
      }
    }
  };

  // Silme için işlem başlatma
  const handleDeleteClick = (faq) => {
    setFaqToDelete(faq);
    setDeleteDialogOpen(true);
  };

  // Silme işlemini onaylama
  const handleConfirmDelete = async () => {
    try {
      console.log(faqToDelete);
      const res = await Services.RemoveFaq(token, +faqToDelete.id);
      console.log(res.data);
      if (res.data.status) {
        toast.success('İşlem başarıyla gerçekleşmiştir.');
        fetchFaqData();
        setDeleteDialogOpen(false);
      } else {
        toast.error('İşlem gerçekleştirilirken bir hata oluştu.');
      }
    } catch (error) {
      console.error(error);
      toast.error('İşlem gerçekleştirilirken bir hata oluştu.');
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const TABLE_HEAD = [
    {
      id: 'title',
      label: 'Başlık',
      alignRight: false,
    },
    {
      id: 'description',
      label: 'Açıklama',
      alignRight: false,
    },
    {
      id: 'type',
      label: 'Tip',
      alignRight: false,
      format: (value, row) => {
        if (row.type === 'porter') return 'Porter';
        else if (row.type === 'store') return 'Mağaza';
        else return row.type;
      },
    },
    {
      id: 'actions',
      label: '',
      alignRight: true,
      format: (value, row) => (
        <>
          <IconButton onClick={() => handleOpenDialogForEdit(row)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDeleteClick(row)}>
            <DeleteIcon color="error" />
          </IconButton>
        </>
      ),
    },
  ];

  if (loading) {
    return (
      <Stack height="100vh" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <Helmet>
        <title>SSS Yönetimi</title>
      </Helmet>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Üst Tab Bölümü */}
        <Stack
          direction="row"
          sx={{
            width: 300,
            border: '1px solid #EAECF0',
            borderRadius: '12px',
            overflow: 'hidden',
            mb: 2,
          }}
        >
          <Box
            flex={1}
            textAlign="center"
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ cursor: 'pointer', p: '5px' }}
            onClick={() => handleTabChange('porter')}
          >
            <Box
              sx={{
                fontWeight: selectedTab === 'porter' ? '600' : '500',
                color: selectedTab === 'porter' ? '#3A4A70' : '#667085',
                backgroundColor:
                  selectedTab === 'porter' ? '#D8E8FF' : 'transparent',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: '10px',
                borderRadius: '6px',
                boxShadow:
                  selectedTab === 'porter'
                    ? '0px 1px 2px 0px #1018280F, 0px 1px 3px 0px #1018281A'
                    : 'none',
              }}
            >
              Porter
            </Box>
          </Box>
          <Box
            flex={1}
            textAlign="center"
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ cursor: 'pointer', p: '5px' }}
            onClick={() => handleTabChange('store')}
          >
            <Box
              sx={{
                fontWeight: selectedTab === 'store' ? '600' : '500',
                color: selectedTab === 'store' ? '#3A4A70' : '#667085',
                backgroundColor:
                  selectedTab === 'store' ? '#D8E8FF' : 'transparent',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: '10px',
                borderRadius: '6px',
                boxShadow:
                  selectedTab === 'store'
                    ? '0px 1px 2px 0px #1018280F, 0px 1px 3px 0px #1018281A'
                    : 'none',
              }}
            >
              Mağaza
            </Box>
          </Box>
        </Stack>

        {/* Üst Kısım: Başlık & Ekle Butonu */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h4">SSS Yönetimi</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenDialogForNew}
          >
            Ekle
          </Button>
        </Stack>

        {/* DynamicTable ile FAQ Listesi */}
        <DynamicTable
          data={faqData}
          headers={TABLE_HEAD}
          initialOrder={'title'}
          initalOrderDirection={'asc'}
          isOrdered={true}
          isMobile={isMobile}
          rowOnClick={(row) => handleOpenDialogForEdit(row)}
        />

        {/* Add/Edit FAQ Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {modalType === 'new' ? 'FAQ Oluştur' : 'FAQ Düzenle'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Başlık"
                fullWidth
                value={selectedFaq.title}
                onChange={(e) =>
                  setSelectedFaq({ ...selectedFaq, title: e.target.value })
                }
              />
              <TextField
                label="Açıklama"
                fullWidth
                multiline
                rows={4}
                value={selectedFaq.description}
                onChange={(e) =>
                  setSelectedFaq({
                    ...selectedFaq,
                    description: e.target.value,
                  })
                }
              />
              <FormControl fullWidth>
                <InputLabel>Tip</InputLabel>
                <Select
                  label="Type"
                  value={selectedFaq.type}
                  onChange={(e) =>
                    setSelectedFaq({ ...selectedFaq, type: e.target.value })
                  }
                >
                  <MenuItem value="porter">Porter</MenuItem>
                  <MenuItem value="store">Mağaza</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>İptal</Button>
            <Button
              onClick={handleDialogSubmit}
              variant="contained"
              color="primary"
            >
              {modalType === 'new' ? 'Oluştur' : 'Kaydet'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Silme Onay Dialog'u */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
          <DialogTitle>Uyarı</DialogTitle>
          <DialogContentText style={{ padding: 20 }}>
            Kaldırmak istediğinize emin misiniz?
          </DialogContentText>
          <DialogActions>
            <Button onClick={handleDeleteDialogClose}>İptal</Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
            >
              Evet
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
